/** @file buildDocPreview: a puppet document plus its image bytes built into bundle files locally. */
import type { PuppetDoc } from '../state/puppetDoc';
import { buildBundle } from '@zilo/pose3d-export/buildBundle';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { packShelves } from '@zilo/pose3d-image-io/pack/packShelves';
import { decodePng } from '@zilo/pose3d-image-io/png/decodePng';
import { encodePng } from '@zilo/pose3d-image-io/png/encodePng';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { webZlibCodec } from '@zilo/pose3d-image-io/zlib/webZlibCodec';

/** The bundle files and export's warnings (empty slots, unknown clip nodes). */
export type DocPreview = { files: Record<string, Uint8Array | string>; warnings: Warning[] };

/**
 * Decodes every image (PNG bytes by image id, image-io `decodePng` with `webZlibCodec`) and runs export's
 * `buildBundle` with image-io `packShelves` and `encodePng`; `now` is 0 (a preview has no creation time).
 */
export async function buildDocPreview(doc: PuppetDoc, images: Readonly<Record<string, Uint8Array>>): Promise<DocPreview> {
  const decoded = await Promise.all(Object.entries(images).map(async ([id, bytes]) => [id, await decodePng(bytes, webZlibCodec)] as const));
  const pixels: Record<string, { size: [number, number]; pixels: Image }> = {};
  for (const [id, image] of decoded) pixels[id] = { size: [image.width, image.height], pixels: image };
  const input = { id: doc.id, name: doc.name, revision: doc.revision, puppet: doc.puppet, clips: doc.clips };
  const { files, warnings } = await buildBundle(input, { packShelves, encodePng: (image) => encodePng(image), images: pixels, now: 0 });
  return { files, warnings };
}
